<template>
  <div class="container">
    <h1>图片压缩工具</h1>
    
    <div class="upload-area" @click="triggerFileInput" @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop">
      <div class="loading" :class="{ active: isCompressing }">
        <div class="spinner"></div>
        <p>正在压缩图片...</p>
      </div>
      
      <i class="fas fa-cloud-upload-alt"></i>
      <p>点击或拖拽图片到此处上传</p>
      <p class="error" v-if="errorMessage">{{ errorMessage }}</p>
      <input type="file" ref="fileInput" @change="handleFileChange" accept="image/*" style="display: none;">
    </div>
    
    <div class="controls">
      <div class="control-group">
        <label for="quality">压缩质量 (0-1)</label>
        <input type="range" id="quality" min="0" max="1" step="0.05" v-model.number="quality" @input="updateQualityDisplay">
        <div class="value-display">{{ quality }}</div>
      </div>
      
      <div class="control-group">
        <label for="maxWidth">最大宽度 (px)</label>
        <input type="range" id="maxWidth" min="100" max="2000" step="50" v-model.number="maxWidth" @input="updateMaxWidthDisplay">
        <div class="value-display">{{ maxWidth }} px</div>
      </div>
      
      <div class="control-group">
        <label for="maxHeight">最大高度 (px)</label>
        <input type="range" id="maxHeight" min="100" max="2000" step="50" v-model.number="maxHeight" @input="updateMaxHeightDisplay">
        <div class="value-display">{{ maxHeight }} px</div>
      </div>
      
      <div class="control-group">
        <label for="outputType">输出格式</label>
        <select id="outputType" v-model="outputType">
          <option value="auto">自动</option>
          <option value="image/jpeg">JPEG</option>
          <option value="image/png">PNG</option>
          <option value="image/webp">WebP</option>
        </select>
      </div>
    </div>
    
    <button @click="compressImage" :disabled="!originalFile || isCompressing">压缩图片</button>
    
    <div class="preview-container" v-if="showPreview">
      <div class="preview-box">
        <h3 class="preview-title">原始图片</h3>
        <img :src="originalPreview" class="image-preview" v-if="originalPreview">
        <div class="file-info">
          <p>尺寸: <span>{{ originalDimensions }}</span></p>
          <p>大小: <span>{{ formatFileSize(originalSize) }}</span></p>
        </div>
      </div>
      
      <div class="preview-box">
        <h3 class="preview-title">压缩后图片</h3>
        <img :src="compressedPreview" class="image-preview" v-if="compressedPreview">
        <div class="file-info" v-if="compressedFile">
          <p>尺寸: <span>{{ compressedDimensions }}</span></p>
          <p>大小: <span>{{ formatFileSize(compressedSize) }}</span></p>
          <p>压缩率: <span>{{ compressionRatio }}%</span></p>
        </div>
      </div>
    </div>
    
    <button @click="downloadFile" v-if="compressedFile" style="margin-top: 20px;">下载压缩后的图片</button>
  </div>
</template>

<script>
import Compressor from 'compressorjs';

export default {
  name: 'PictureCompressionView',
  data() {
    return {
      quality: 0.8,
      maxWidth: 1000,
      maxHeight: 1000,
      outputType: 'auto',
      originalFile: null,
      compressedFile: null,
      originalPreview: '',
      compressedPreview: '',
      originalDimensions: '-',
      compressedDimensions: '-',
      originalSize: 0,
      compressedSize: 0,
      compressionRatio: '-',
      isCompressing: false,
      errorMessage: '',
      showPreview: false
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleDragOver(e) {
      e.preventDefault();
      e.target.style.borderColor = '#4a90e2';
      e.target.style.backgroundColor = '#f9f9f9';
    },
    
    handleDragLeave(e) {
      e.target.style.borderColor = '#ccc';
      e.target.style.backgroundColor = 'transparent';
    },
    
    handleDrop(e) {
      e.preventDefault();
      e.target.style.borderColor = '#ccc';
      e.target.style.backgroundColor = 'transparent';
      
      if (e.dataTransfer.files.length) {
        this.handleFile(e.dataTransfer.files[0]);
      }
    },
    
    handleFileChange(e) {
      if (e.target.files.length) {
        this.handleFile(e.target.files[0]);
      }
    },
    
    handleFile(file) {
      // 检查文件类型
      if (!file.type.match('image.*')) {
        this.errorMessage = '请选择图片文件';
        return;
      }
      
      // 检查文件大小 (限制10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = '图片大小不能超过10MB';
        return;
      }
      
      // 清除错误信息
      this.errorMessage = '';
      
      // 显示原始图片预览
      const reader = new FileReader();
      reader.onload = (e) => {
        this.originalPreview = e.target.result;
        
        // 获取图片尺寸
        const img = new Image();
        img.onload = () => {
          this.originalDimensions = `${img.width} × ${img.height}`;
        };
        img.src = e.target.result;
        
        // 显示文件大小
        this.originalSize = file.size;
      };
      reader.readAsDataURL(file);
      
      // 存储原始文件
      this.originalFile = file;
      
      // 隐藏预览容器(如果有之前的结果)
      this.showPreview = false;
    },
    
    updateQualityDisplay() {
      // 已经在v-model中自动更新
    },
    
    updateMaxWidthDisplay() {
      // 已经在v-model中自动更新
    },
    
    updateMaxHeightDisplay() {
      // 已经在v-model中自动更新
    },
    
    compressImage() {
      if (!this.originalFile) return;
      
      // 显示加载状态
      this.isCompressing = true;
      
      // 获取压缩选项
      const options = {
        quality: this.quality,
        width: this.maxWidth,
        height: this.maxHeight,
        mimeType: this.outputType === 'auto' ? undefined : this.outputType,
        success: (result) => {
          // 压缩完成
          this.isCompressing = false;
          
          // 存储压缩后的文件
          this.compressedFile = result;
          
          // 显示压缩后的图片预览
          const reader = new FileReader();
          reader.onload = (e) => {
            this.compressedPreview = e.target.result;
            
            // 获取压缩后图片尺寸
            const img = new Image();
            img.onload = () => {
              this.compressedDimensions = `${img.width} × ${img.height}`;
            };
            img.src = e.target.result;
            
            // 显示压缩后文件大小
            this.compressedSize = result.size;
            
            // 计算压缩率
            this.compressionRatio = ((this.originalSize - result.size) / this.originalSize * 100).toFixed(2);
          };
          reader.readAsDataURL(result);
          
          // 显示预览容器
          this.showPreview = true;
        },
        error: (err) => {
          // 压缩出错
          this.isCompressing = false;
          this.errorMessage = '图片压缩失败: ' + err.message;
          console.error(err);
        }
      };
      
      // 执行压缩
      new Compressor(this.originalFile, options);
    },
    
    downloadFile() {
      if (!this.compressedFile) return;
      
      const a = document.createElement('a');
      const url = URL.createObjectURL(this.compressedFile);
      
      // 生成文件名
      const originalName = this.originalFile.name;
      const ext = this.compressedFile.type.split('/')[1] || originalName.split('.').pop();
      const name = originalName.lastIndexOf('.') > 0 
        ? originalName.substring(0, originalName.lastIndexOf('.')) 
        : originalName;
      const filename = `${name}_compressed.${ext}`;
      
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.container {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  max-width: 800px;
  margin-top: 20px;
}

h1 {
  color: #333;
  text-align: center;
  margin-bottom: 30px;
}

.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
  position: relative;
}

.upload-area:hover {
  border-color: #4a90e2;
  background-color: #f9f9f9;
}

.upload-area i {
  font-size: 48px;
  color: #4a90e2;
  margin-bottom: 15px;
}

.upload-area p {
  color: #666;
  margin-bottom: 10px;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.control-group {
  flex: 1;
  min-width: 200px;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
}

input[type="range"], select {
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

input[type="range"] {
  -webkit-appearance: none;
  height: 8px;
  background: #e0e0e0;
  outline: none;
  border-radius: 4px;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #4a90e2;
  cursor: pointer;
}

.value-display {
  font-size: 14px;
  color: #777;
  text-align: right;
  margin-top: 5px;
}

button {
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
  width: 100%;
  margin-top: 10px;
}

button:hover {
  background-color: #3a7bc8;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 30px;
  justify-content: center;
}

.preview-box {
  flex: 1;
  min-width: 300px;
  text-align: center;
}

.preview-title {
  font-weight: 600;
  color: #444;
  margin-bottom: 10px;
}

.image-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.file-info {
  margin-top: 10px;
  font-size: 14px;
  color: #666;
}

.loading {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 6px;
}

.loading.active {
  display: flex;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #4a90e2;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #e74c3c;
  margin-top: 10px;
  text-align: center;
}
</style>